import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Dialog } from "@headlessui/react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Modal } from "../UI/Modal/Modal.tsx";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { classNames } from "../UI/Utils/classNames.ts";
import {
  SchedulerCloserDTO,
  SchedulerClosersDTO,
  SchedulerExcludedCloserDTO,
  useGetSchedulerClosers,
} from "../HubSpot/useGetSchedulerClosers.ts";
import { isExclusionToFix, schedulerCloserIssues } from "./closerIssues.ts";
import { closerDetailPath } from "./Detail/closerDetailPath.ts";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type SectionKey = "web" | "setter" | "excluded";

type Badge = { label: string; tone: "red" | "amber" | "blue" | "gray" };

const BADGE_CLASS_NAMES: Record<Badge["tone"], string> = {
  red: "bg-red-100 text-red-700",
  amber: "bg-amber-100 text-amber-700",
  blue: "bg-blue-100 text-blue-700",
  gray: "bg-gray-100 text-gray-600",
};

function formatDay(day: string): string {
  return dayjs(day).format("DD/MM/YYYY");
}

function formatCr(cr: number | null): string {
  return cr === null ? "—" : `${Math.round(cr * 100)}%`;
}

function availabilityBadges(closer: SchedulerCloserDTO): Badge[] {
  if (closer.availabilityToday === "blocked" && closer.block) {
    return [
      {
        label: `Bloccato dal ${formatDay(closer.block.from)} al ${formatDay(closer.block.to)}`,
        tone: "amber",
      },
    ];
  }
  if (closer.availabilityToday === "not_started" && closer.bookableFrom) {
    return [
      {
        label: `Prenotabile dal ${formatDay(closer.bookableFrom)}`,
        tone: "amber",
      },
    ];
  }
  return [];
}

function closerBadges(closer: SchedulerCloserDTO): Badge[] {
  return [
    ...schedulerCloserIssues(closer).map(
      (label): Badge => ({ label, tone: "red" }),
    ),
    ...availabilityBadges(closer),
    ...(closer.inTrial ? [{ label: "In prova", tone: "blue" } as Badge] : []),
    {
      label: closer.tierRank
        ? `Tier ${closer.tierRank} · CR ${formatCr(closer.cr)}`
        : `CR ${formatCr(closer.cr)}`,
      tone: "gray",
    },
    ...(closer.capacity === null
      ? []
      : [
          {
            label: closer.usesDefaultCapacity
              ? `Cap ${closer.capacity} (default)`
              : `Cap ${closer.capacity}`,
            tone: "gray",
          } as Badge,
        ]),
  ];
}

function BadgeList({ badges }: { badges: Badge[] }) {
  if (badges.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {badges.map((badge) => (
        <span
          key={badge.label}
          className={classNames(
            "text-xs px-1.5 py-0.5 rounded",
            BADGE_CLASS_NAMES[badge.tone],
          )}
        >
          {badge.label}
        </span>
      ))}
    </div>
  );
}

function CloserCard({
  name,
  email,
  badges,
  onOpen,
}: {
  name: string;
  email: string | null;
  badges: Badge[];
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded-md bg-white border border-gray-200 px-2 py-1.5 min-w-0 text-left hover:border-gray-400"
    >
      <p className="text-sm font-medium truncate">{name}</p>
      <p className="text-xs text-gray-400 truncate">{email ?? "—"}</p>
      <BadgeList badges={badges} />
    </button>
  );
}

function SectionHeaderBadges({ badges }: { badges: Badge[] }) {
  return (
    <>
      {badges.map((badge) => (
        <span
          key={badge.label}
          className={classNames(
            "text-xs px-2 py-0.5 rounded-full tabular-nums",
            BADGE_CLASS_NAMES[badge.tone],
          )}
        >
          {badge.label}
        </span>
      ))}
    </>
  );
}

function bookableSectionBadges(closers: SchedulerCloserDTO[]): Badge[] {
  const withIssues = closers.filter(
    (c) => schedulerCloserIssues(c).length > 0,
  ).length;
  const notBookableToday = closers.filter(
    (c) => c.availabilityToday !== "bookable",
  ).length;
  return [
    ...(withIssues > 0
      ? [{ label: `${withIssues} da sistemare`, tone: "red" } as Badge]
      : []),
    ...(notBookableToday > 0
      ? [
          {
            label: `${notBookableToday} non prenotabili oggi`,
            tone: "amber",
          } as Badge,
        ]
      : []),
    { label: `${closers.length} closer`, tone: "gray" },
  ];
}

function excludedSectionBadges(
  excluded: SchedulerExcludedCloserDTO[],
): Badge[] {
  const toFix = excluded.filter(isExclusionToFix).length;
  return [
    ...(toFix > 0
      ? [{ label: `${toFix} da sistemare`, tone: "red" } as Badge]
      : []),
    { label: `${excluded.length} closer`, tone: "gray" },
  ];
}

export function SchedulerClosersModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const { getSchedulerClosers, loading } = useGetSchedulerClosers();
  const [data, setData] = useState<SchedulerClosersDTO | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<SectionKey[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData((await getSchedulerClosers()) ?? null);
    } catch (err) {
      console.error("Error fetching scheduler closers:", err);
      setError("Errore nel recupero dei closer dello scheduler. Riprova.");
      setData(null);
    }
  }, [getSchedulerClosers]);

  useEffect(() => {
    if (!isOpen) return;
    setExpandedKeys([]);
    load();
  }, [isOpen, load]);

  const toggle = (key: SectionKey) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const webClosers = data?.closers.filter((c) => c.acceptsWeb) ?? [];
  const setterClosers = data?.closers.filter((c) => c.acceptsSetter) ?? [];
  const excluded = data?.excluded ?? [];

  const sections: {
    key: SectionKey;
    title: string;
    subtitle: string;
    badges: Badge[];
    cards: {
      key: string;
      name: string;
      email: string | null;
      badges: Badge[];
    }[];
  }[] = [
    {
      key: "web",
      title: "Prenotabili dalla landing (Auto)",
      subtitle:
        "prenotabile_da Tutti, Web o vuoto: base di check, storico e mail",
      badges: bookableSectionBadges(webClosers),
      cards: webClosers.map((c) => ({
        key: c.closerId,
        name: c.name,
        email: c.email,
        badges: closerBadges(c),
      })),
    },
    {
      key: "setter",
      title: "Prenotabili dai setter",
      subtitle: "prenotabile_da Tutti, Setter o vuoto",
      badges: bookableSectionBadges(setterClosers),
      cards: setterClosers.map((c) => ({
        key: c.closerId,
        name: c.name,
        email: c.email,
        badges: closerBadges(c),
      })),
    },
    {
      key: "excluded",
      title: "Esclusi dallo scheduler",
      subtitle: "Utenti Closer senza i requisiti per essere prenotati",
      badges: excludedSectionBadges(excluded),
      cards: excluded.map((e) => ({
        key: e.closerId,
        name: e.name,
        email: e.email,
        badges: [
          { label: e.reasonLabel, tone: isExclusionToFix(e) ? "red" : "amber" },
        ],
      })),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Closer dello scheduler"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="absolute top-3 right-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-medium leading-6 text-gray-900 pr-8">
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            I closer che lo scheduler considera, letti dagli oggetti Utente di
            HubSpot. Clicca su una sezione per vederne i closer.
          </p>
          {data && (
            <p className="text-xs text-gray-400 mt-1">
              Cap di default:{" "}
              {data.capacityDefault === null
                ? "non impostato"
                : data.capacityDefault}{" "}
              · Prova: {data.trialDays} giorni
            </p>
          )}
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      {loading && <LoadingRows rows={3} />}

      {!loading && error && <ErrorFeedback title="Errore" message={error} />}

      {!loading && !error && data && (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {sections.map((section) => {
            const isExpanded = expandedKeys.includes(section.key);
            const canExpand = section.cards.length > 0;
            return (
              <div
                key={section.key}
                className="rounded-lg border border-gray-200 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(section.key)}
                  disabled={!canExpand}
                  className={classNames(
                    "w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left",
                    canExpand
                      ? "hover:bg-gray-50 cursor-pointer"
                      : "cursor-default",
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {section.title}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {section.subtitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <SectionHeaderBadges badges={section.badges} />
                    {canExpand && (
                      <ChevronDownIcon
                        className={classNames(
                          "w-4 h-4 text-gray-400 transition-transform",
                          isExpanded ? "rotate-180" : "",
                        )}
                      />
                    )}
                  </div>
                </button>

                {isExpanded && canExpand && (
                  <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 grid gap-1 sm:grid-cols-2">
                    {section.cards.map((card) => (
                      <CloserCard
                        key={card.key}
                        name={card.name}
                        email={card.email}
                        badges={card.badges}
                        onOpen={() => {
                          onClose();
                          navigate(closerDetailPath(card.key));
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
